import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Grid,
  Button,
  Typography,
  Skeleton,
  useTheme,
  Tooltip,
} from '@mui/material';
import ConfirmationModal from '../dialogs/ConfirmationModal';
import { maskPhoneNumber } from '../../utils/phone';
import { rememberEnrollmentIntent } from '../../login/policy/enrollmentIntent';

export default function MFA(props) {
  const { t } = useTranslation();
  const theme = useTheme();
  const { userProfile, isLoading, revokeMFA, logOut } = props;
  const [open, setOpen] = useState(false);
  // Which factor the dialog is about to add, or null when it is about to remove
  // one: the two are the same click on opposite states of the same row.
  const [enrollFactor, setEnrollFactor] = useState(null);
  const [revokeEnrollmentId, setRevokeEnrollmentId] = useState(null);
  const [removeErrorKey, setRemoveErrorKey] = useState(null);

  const phoneFactor = userProfile?.mfaInfo?.find(factor => factor.phoneInfo);
  const totpFactor = userProfile?.mfaInfo?.find(factor => factor.totpInfo);

  const styles = {
    typography: {
      secondary: {
        color:
          theme.palette.type === 'light'
            ? 'rgba(0, 0, 0, 0.6)'
            : 'rgba(255, 255, 255, 0.6)',
      },
    },
  };

  const handleRemoveMFA = async () => {
    setOpen(false);
    setRemoveErrorKey(null);
    const tenantId = userProfile.tenantId;
    const body = revokeEnrollmentId
      ? { tenantId, mfaEnrollmentId: revokeEnrollmentId }
      : { tenantId };
    const revoked = await revokeMFA(body);
    // The factor is still on the account. Signing out here would hide the
    // failure behind a fresh login and leave the user believing it is gone.
    if (!revoked) {
      setRemoveErrorKey('heimdal_app.Profile.remove_mfa_error');
      return;
    }
    logOut().then(() => {
      window.location.reload();
    });
  };

  const handleEnrollMFA = async () => {
    setOpen(false);
    setRemoveErrorKey(null);
    // Bound to the account that asked: the request outlives this page, and the
    // login screen must not hand it to whoever signs in next on this browser.
    rememberEnrollmentIntent({
      factor: enrollFactor,
      uid: userProfile?.uid,
      tenantId: userProfile?.tenantId,
    });
    localStorage.removeItem('date_expiration');
    logOut().then(() => {
      window.location.reload();
    });
  };

  return (
    <>
      <Grid
        container
        spacing={2}
        paddingY={2}
        paddingX={4}
        justifyContent={'space-between'}>
        <Tooltip
          title={t('heimdal_app.Profile.multi_factor_authentication_info')}
          arrow
          placement="left">
          <Grid item xs={12} md={12}>
            {isLoading ? (
              <Skeleton animation="wave" height={24} width="20%" />
            ) : (
              <Typography variant="h6" textAlign={'left'}>
                {t('heimdal_app.Profile.multi_factor_authentication')}
              </Typography>
            )}
          </Grid>
        </Tooltip>
        <Grid container item xs={12} md={12} spacing={2}>
          <Grid item xs={8}>
            {isLoading ? (
              <Skeleton animation="wave" height={24} width="60%" />
            ) : (
              <Typography variant="subtitle2" textAlign={'left'}>
                {t('heimdal_app.Profile.text_message')}
              </Typography>
            )}
            {isLoading ? (
              <Skeleton animation="wave" height={24} width="60%" />
            ) : (
              <Typography
                variant="body2"
                textAlign={'left'}
                sx={styles.typography.secondary}>
                {phoneFactor?.phoneInfo
                  ? maskPhoneNumber(phoneFactor.phoneInfo)
                  : t('heimdal_app.Profile.not_set')}
              </Typography>
            )}
          </Grid>
          <Grid
            item
            xs={4}
            justifyContent={'flex-end'}
            alignItems={'flex-end'}
            display={'flex'}>
            {isLoading ? (
              <Skeleton animation="wave" height={48} width="100%" />
            ) : (
              <Tooltip
                title={
                  phoneFactor
                    ? t('heimdal_app.Profile.delete_mfa_info')
                    : t('heimdal_app.Profile.add_mfa_info')
                }
                arrow
                placement="left">
                <Button
                  variant="contained"
                  size="small"
                  style={{ textTransform: 'capitalize' }}
                  aria-label={t(
                    phoneFactor
                      ? 'heimdal_app.Profile.delete_factor'
                      : 'heimdal_app.Profile.add_factor',
                    { factor: t('heimdal_app.Profile.text_message') },
                  )}
                  onClick={() => {
                    setOpen(true);
                    setEnrollFactor(phoneFactor ? null : 'sms');
                    setRevokeEnrollmentId(phoneFactor?.mfaEnrollmentId ?? null);
                  }}>
                  <Typography variant={'inherit'}>
                    {phoneFactor
                      ? t('heimdal_app.Profile.delete')
                      : t('heimdal_app.Profile.add')}
                  </Typography>
                </Button>
              </Tooltip>
            )}
          </Grid>
        </Grid>
        <Grid container item xs={12} md={12} spacing={2}>
          <Grid item xs={8}>
            {isLoading ? (
              <Skeleton animation="wave" height={24} width="60%" />
            ) : (
              <Typography variant="subtitle2" textAlign={'left'}>
                {t('heimdal_app.TOTP.authenticator_app')}
              </Typography>
            )}
            {isLoading ? (
              <Skeleton animation="wave" height={24} width="60%" />
            ) : (
              <Typography
                variant="body2"
                textAlign={'left'}
                sx={styles.typography.secondary}>
                {totpFactor
                  ? t('heimdal_app.TOTP.configured')
                  : t('heimdal_app.Profile.not_set')}
              </Typography>
            )}
          </Grid>
          <Grid
            item
            xs={4}
            justifyContent={'flex-end'}
            alignItems={'flex-end'}
            display={'flex'}>
            {isLoading ? (
              <Skeleton animation="wave" height={48} width="100%" />
            ) : (
              <Button
                variant="contained"
                size="small"
                style={{ textTransform: 'capitalize' }}
                aria-label={t(
                  totpFactor
                    ? 'heimdal_app.Profile.delete_factor'
                    : 'heimdal_app.Profile.add_factor',
                  { factor: t('heimdal_app.TOTP.authenticator_app') },
                )}
                onClick={() => {
                  setOpen(true);
                  setEnrollFactor(totpFactor ? null : 'totp');
                  setRevokeEnrollmentId(totpFactor?.mfaEnrollmentId ?? null);
                }}>
                <Typography variant={'inherit'}>
                  {totpFactor
                    ? t('heimdal_app.Profile.delete')
                    : t('heimdal_app.Profile.add')}
                </Typography>
              </Button>
            )}
          </Grid>
        </Grid>
        {removeErrorKey && (
          <Grid item xs={12} md={12}>
            <Typography
              role="alert"
              variant="body2"
              color="error"
              textAlign={'left'}>
              {t(removeErrorKey)}
            </Typography>
          </Grid>
        )}
      </Grid>
      {isLoading ? null : (
        <ConfirmationModal
          open={open}
          setOpen={setOpen}
          onVerificationComplete={enrollFactor ? handleEnrollMFA : handleRemoveMFA}
          title={t('heimdal_app.Profile.multi_factor_authentication')}
          content={
            enrollFactor
              ? t('heimdal_app.Profile.enroll_mfa_msg')
              : t('heimdal_app.Profile.remove_mfa_msg')
          }
        />
      )}
    </>
  );
}
